/* La fiche d'un agent : ce qu'il porte, ce qu'il a vendu, ce qu'on lui doit,
   et à quels prix il a le droit de vendre.
 *
 * C'est la fiche qu'on ouvre avant un point mensuel : elle doit répondre sans
 * qu'on aille chercher ailleurs. */

import { Fragment, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useAgents, useClients, useCommissions, useParts, usePrixAgent, useProduits, useUsines,
  useVentes,
} from "@/lib/donnees";
import { useNiveau, useSession } from "@/lib/session";
import { Empty, Kpi, Kpis, Onglets, Section, Tag } from "@/ui/primitives";
import { Identite } from "@/ui/Fiche";
import { Icon } from "@/ui/Icon";
import { Table, dansUnChamp } from "@/ui/Table";
import { useToast } from "@/ui/Toast";
import { dateFr, entier, euro, euroCourt, euroPrecis, pourcent } from "@/lib/format";
import { GROUPES_AGENT, GROUPES_MES_COORDONNEES } from "@/domain/fiches";
import { enVigueur } from "@/domain/prix";
import { etapeDe } from "@/domain/ventes";
import { etatCommissionDe } from "@/domain/commissions";
import { Journal } from "@/features/journal/Journal";
import { enregistrerFiche } from "@/features/journal/actions";
import { majMesCoordonnees } from "@/features/espace-usine/actions";
import { inviter } from "./actions";
import type { Profil } from "@/types/metier";

type Onglet = "fiche" | "ventes" | "commissions" | "planchers" | "clients" | "suivi";

export function FicheAgent() {
  const { id } = useParams<{ id: string }>();
  const navigue = useNavigate();
  const niveau = useNiveau();
  const toast = useToast();
  const { profil } = useSession();
  const direction = niveau === "admin";

  const { donnees: agents, chargement, erreur, recharger } = useAgents();
  const { donnees: ventes } = useVentes();
  const { donnees: commissions } = useCommissions();
  const { donnees: parts } = useParts();
  const { donnees: planchers } = usePrixAgent();
  const { donnees: produits } = useProduits();
  const { donnees: clients } = useClients();
  const { donnees: usines } = useUsines();
  const [onglet, setOnglet] = useState<Onglet>("fiche");
  const [envoi, setEnvoi] = useState(false);

  const agent = useMemo(() => agents.find((a) => a.id === id) ?? null, [agents, id]);

  const nomClient = useMemo(() => new Map(clients.map((c) => [c.id, c.nom] as const)), [clients]);
  const nomUsine = useMemo(() => new Map(usines.map((u) => [u.id, u.nom] as const)), [usines]);
  const refProduit = useMemo(() => new Map(produits.map((p) => [p.id, p] as const)), [produits]);

  const sesVentes = useMemo(() => ventes.filter((v) => v.agent_id === id), [ventes, id]);
  const sesComm = useMemo(() => commissions.filter((c) => c.agent_id === id), [commissions, id]);
  const sesParts = useMemo(() => parts.filter((p) => p.agent_id === id), [parts, id]);
  const sesPlanchers = useMemo(
    () => enVigueur(planchers.filter((p) => p.agent_id === id)),
    [planchers, id],
  );

  /* Ses clients : ceux qu'il a servis, et ce qu'ils lui ont rapporté. */
  const sesClients = useMemo(() => {
    const m = new Map<string, { id: string; nom: string; nb: number; montant: number; derniere: string }>();
    for (const v of sesVentes) {
      const cid = v.client_id ?? "—";
      const e = m.get(cid) ?? {
        id: cid, nom: v.client_id ? (nomClient.get(v.client_id) ?? "—") : "—",
        nb: 0, montant: 0, derniere: "",
      };
      e.nb += 1;
      e.montant = Math.round((e.montant + Number(v.montant)) * 100) / 100;
      if (v.date_document > e.derniere) e.derniere = v.date_document;
      m.set(cid, e);
    }
    return [...m.values()].sort((a, b) => b.montant - a.montant);
  }, [sesVentes, nomClient]);

  if (!direction && profil?.id !== id) {
    return (
      <div className="pad">
        <Empty
          titre="Réservé à la direction"
          detail="Chacun voit sa propre fiche ; les autres se consultent depuis un compte de direction."
        />
      </div>
    );
  }
  if (erreur) return <div className="pad"><div className="banner">{erreur}</div></div>;
  if (chargement) return <div className="pad"><div className="chargement">Chargement…</div></div>;
  if (!agent) {
    return (
      <div className="pad">
        <Empty titre="Agent introuvable" detail="Choisissez une fiche dans la liste." />
      </div>
    );
  }

  const sienne = profil?.id === agent.id;

  /* Deux écritures pour une même fiche, et ce n'est pas un doublon : la
     direction pose ce qu'elle veut, l'intéressé ne corrige que ses
     coordonnées. Le second passe par une fonction qui ne nomme que deux
     colonnes — une politique d'écriture, elle, contrôlerait la ligne et
     laisserait n'importe qui se nommer direction dans le même geste. */
  const enregistrer = async (champs: Partial<Profil>) => {
    if (!profil) throw new Error("Profil inconnu : reconnectez-vous.");
    if (direction) {
      await enregistrerFiche<Profil>(
        { sujet: "profils", sujetId: agent.id, auteur: profil.id },
        "profils",
        agent as unknown as Record<string, unknown>,
        champs as Record<string, unknown>,
      );
    } else {
      await majMesCoordonnees(
        (champs.nom ?? agent.nom) as string,
        (champs.telephone ?? agent.telephone ?? null) as string | null,
      );
    }
    recharger();
    toast("Fiche enregistrée.");
  };

  const envoyer = async () => {
    setEnvoi(true);
    try {
      await inviter(agent);
      toast(`Invitation envoyée à ${agent.email}.`);
      recharger();
    } catch (e) {
      toast(e instanceof Error ? e.message : "L'invitation a échoué.");
    } finally {
      setEnvoi(false);
    }
  };

  const ca = sesVentes.reduce((s, v) => s + Number(v.montant), 0);
  const devis = sesVentes.filter((v) => v.etape === "devis");
  const du = sesComm
    .filter((c) => c.etat !== "payee")
    .reduce((s, c) => s + Number(c.agent_montant), 0);
  const gagne = sesComm.reduce((s, c) => s + Number(c.agent_montant), 0);
  const surplus = sesComm.reduce((s, c) => s + Number(c.surplus_agent ?? 0), 0);

  const etat = !agent.actif
    ? { nom: "suspendu", ton: "crit" }
    : !agent.compte_id
      ? { nom: agent.invite_le ? "invité" : "sans compte", ton: "warn" }
      : { nom: "actif", ton: "ok" };

  const onglets: { cle: Onglet; nom: string; n?: number }[] = [
    { cle: "fiche", nom: "Fiche" },
    { cle: "ventes", nom: "Ventes", n: sesVentes.length },
    { cle: "commissions", nom: "Commissions", n: sesComm.length },
    { cle: "planchers", nom: "Ses prix", n: sesPlanchers.length },
    { cle: "clients", nom: "Clients", n: sesClients.length },
    { cle: "suivi", nom: "Suivi" },
  ];

  return (
    <div className="pad">
      <div className="fiche-tete">
        <span className="avatar avatar-g">
          {agent.nom.split(" ").map((m) => m[0] ?? "").join("").slice(0, 2).toUpperCase()}
        </span>
        <div className="fiche-t">
          <div className="section-t">{agent.nom}</div>
          <div className="section-n">{agent.email}</div>
        </div>
        <div className="fiche-a fiche-a-centre">
          <Tag ton={etat.ton} dot>{etat.nom}</Tag>
          {direction && !agent.compte_id ? (
            <button type="button" className="btn" disabled={envoi} onClick={() => void envoyer()}>
              <Icon n="envoyer" cls="" />
              {envoi ? "Envoi…" : agent.invite_le ? "Relancer l'invitation" : "Inviter"}
            </button>
          ) : null}
          <button type="button" className="btn" onClick={() => navigue("/agents")}>
            <Icon n="retour" cls="" />
            Tous les agents
          </button>
        </div>
      </div>

      <div className="fiche-parcours">
        <Onglets items={onglets} actif={onglet} onChange={setOnglet} />
      </div>

      {onglet === "fiche" ? (
        <>
          <Kpis>
            <Kpi k="Chiffre d'affaires porté" v={euroCourt(ca)} n={`${sesVentes.length} documents`} />
            <Kpi
              k="Pipeline"
              v={euroCourt(devis.reduce((s, v) => s + Number(v.montant), 0))}
              n={`${devis.length} devis en cours`}
            />
            <Kpi k="Commissions gagnées" v={euroCourt(gagne)} n={`dont ${euroCourt(surplus)} d'excédent`} />
            <Kpi k="Reste dû" v={euroCourt(du)} n="parts non réglées" />
          </Kpis>

          <Identite
            valeur={agent}
            groupes={direction ? GROUPES_AGENT : GROUPES_MES_COORDONNEES}
            modifiable={direction || sienne}
            enregistrer={enregistrer}
          />

          <div className="grid2">
            <div>
              <Section titre="Compte de connexion">
                <div className="card card-pad">
                  <dl className="kv">
                    <dt>État</dt>
                    <dd><Tag ton={etat.ton} dot>{etat.nom}</Tag></dd>
                    <dt>Compte rattaché</dt>
                    <dd>{agent.compte_id ? "oui" : "non"}</dd>
                    <dt>Invité le</dt>
                    <dd className="mono">{agent.invite_le ? dateFr(agent.invite_le) : "—"}</dd>
                  </dl>
                </div>
              </Section>
            </div>
            <div>
              <Section titre="Ses parts" note="ce qu'il garde sur la commission">
                <div className="card card-pad">
                  {sesParts.length ? (
                    <dl className="kv">
                      {sesParts.map((p) => (
                        <Fragment key={p.id}>
                          <dt>
                            {p.usine_id ? (nomUsine.get(p.usine_id) ?? "—") : "Toutes usines"}
                          </dt>
                          <dd className="mono">
                            {p.mode === "unite"
                              ? `${euroPrecis(Number(p.valeur))} / unité`
                              : p.mode === "points"
                                ? `${String(p.valeur).replace(".", ",")} point(s)`
                                : pourcent(Number(p.valeur))}
                            {p.actif ? "" : " (inactive)"}
                          </dd>
                        </Fragment>
                      ))}
                    </dl>
                  ) : (
                    <Empty titre="Aucune part propre" detail="Il suit le barème général." />
                  )}
                </div>
              </Section>
            </div>
          </div>
        </>
      ) : null}

      {onglet === "ventes" ? (
        <Section titre="Ses ventes" note={`${sesVentes.length} documents`}>
          <Table
            lignes={sesVentes}
            cle={(v) => v.id}
            chercher="Document ou client"
            onClic={(v, ev) => {
              if (!dansUnChamp(ev)) navigue(`/ventes/${v.id}`);
            }}
            colonnes={[
              { cle: "reference", titre: "Document", type: "ref", filtre: "texte",
                sous: (v) => (v.client_id ? (nomClient.get(v.client_id) ?? "—") : "—") },
              { cle: "client_id", titre: "Client", filtre: "select", mobile: false,
                texte: (v) => (v.client_id ? (nomClient.get(v.client_id) ?? "—") : "—") },
              { cle: "montant", titre: "Montant HT", court: "Montant", type: "euro", total: true,
                nombre: (v) => Number(v.montant) },
              { cle: "etape", titre: "Étape", filtre: "select",
                texte: (v) => etapeDe(v.etape).nom,
                rendu: (v) => <Tag ton={etapeDe(v.etape).ton} dot>{etapeDe(v.etape).court}</Tag> },
              { cle: "date_document", titre: "Date", type: "date", mobile: false,
                rendu: (v) => <span className="mono">{dateFr(v.date_document)}</span> },
            ]}
          />
        </Section>
      ) : null}

      {onglet === "commissions" ? (
        <Section titre="Ses commissions" note={`${sesComm.length} lignes`}>
          <Table
            lignes={sesComm}
            cle={(c) => c.id}
            chercher="Référence"
            colonnes={[
              { cle: "reference", titre: "Commission", court: "Comm.", type: "ref",
                filtre: "texte", sous: (c) => etatCommissionDe(c.etat).nom },
              { cle: "base", titre: "Base", type: "euro", total: true, mobile: false,
                nombre: (c) => Number(c.base) },
              { cle: "agent_montant", titre: "Sa part", type: "euro", total: true,
                nombre: (c) => Number(c.agent_montant) },
              { cle: "surplus_agent", titre: "Dont excédent", court: "Excédent", type: "euro",
                total: true, mobile: false, nombre: (c) => Number(c.surplus_agent ?? 0) },
              { cle: "etat", titre: "État", filtre: "select",
                texte: (c) => etatCommissionDe(c.etat).nom,
                rendu: (c) => {
                  const e = etatCommissionDe(c.etat);
                  return <Tag ton={e.ton} dot>{e.nom}</Tag>;
                } },
            ]}
          />
        </Section>
      ) : null}

      {onglet === "planchers" ? (
        <Section
          titre="Ses planchers"
          note="le minimum auquel il peut vendre, référence par référence"
        >
          {sesPlanchers.length ? (
            <Table
              lignes={sesPlanchers}
              cle={(p) => p.id}
              chercher="Référence"
              onClic={(p, ev) => {
                if (!dansUnChamp(ev)) navigue(`/catalogue/${p.produit_id}`);
              }}
              colonnes={[
                { cle: "produit_id", titre: "Référence", type: "ref", filtre: "texte",
                  texte: (p) => refProduit.get(p.produit_id)?.ref ?? "—",
                  sous: (p) => refProduit.get(p.produit_id)?.nom ?? "" },
                { cle: "nom", titre: "Désignation", classe: "name", mobile: false,
                  texte: (p) => refProduit.get(p.produit_id)?.nom ?? "—" },
                { cle: "prix_mini", titre: "Plancher", type: "euro", total: true,
                  agregat: "moyenne", totalTitre: "Plancher moyen",
                  nombre: (p) => Number(p.prix_mini) },
                { cle: "catalogue", titre: "Prix catalogue", court: "Catalogue", type: "euro",
                  mobile: false,
                  nombre: (p) => Number(refProduit.get(p.produit_id)?.prix ?? 0) },
                { cle: "debut_le", titre: "Depuis", type: "date", mobile: false },
              ]}
            />
          ) : (
            <Empty
              titre="Aucun plancher propre"
              detail="À défaut, le prix du catalogue fait plancher."
            />
          )}
        </Section>
      ) : null}

      {onglet === "clients" ? (
        <Section titre="Ses clients" note="cumul de ce qu'il leur a vendu">
          <Table
            lignes={sesClients}
            cle={(c) => c.id}
            onClic={(c, ev) => {
              if (!dansUnChamp(ev) && c.id !== "—") navigue(`/clients/${c.id}`);
            }}
            colonnes={[
              { cle: "nom", titre: "Client", filtre: "texte",
                sous: (c) => `${c.nb} ${c.nb > 1 ? "ventes" : "vente"}` },
              { cle: "nb", titre: "Ventes", type: "num", total: true, mobile: false },
              { cle: "montant", titre: "Chiffre d'affaires", court: "CA", type: "euro",
                total: true },
              { cle: "derniere", titre: "Dernière vente", court: "Dernière", type: "date",
                mobile: false },
            ]}
          />
        </Section>
      ) : null}

      {onglet === "suivi" ? <Journal sujet="profils" sujetId={agent.id} /> : null}

      <div className="vide-ligne">
        {entier(sesVentes.length)} documents portés · <b className="mono">{euro(ca)}</b> de chiffre
        d'affaires · <b className="mono">{euro(du)}</b> encore dû.
      </div>
    </div>
  );
}
